import { useCallback, useRef } from 'react'
import type { Node, Edge } from '@xyflow/react'
import { useReactFlow } from '@xyflow/react'
// html-to-image is lazy-loaded in export function to reduce bundle size
import {
  sortNodesByHierarchy,
  getChildNodes,
  calculateChildrenBounds,
  absoluteToRelativePosition,
  relativeToAbsolutePosition,
  toggleGroupVisibility,
  hideEdgesForHiddenNodes,
  groupSelectedNodes,
  ungroupNodes,
  relativeToAbsolutePositionDeep,
  absoluteToRelativePositionDeep,
  calculateOptimalGroupBounds,
  recalculateGroupWithChildren,
  getGroupsToResize,
  findBestDropTarget,
  wouldCreateCircularReference,
  DEFAULT_SUBFLOW_CONFIG,
  type SubflowConfig,
} from '@/lib/subflow-utils'

// Threshold for position changes to trigger resize (prevents micro-adjustments)
const POSITION_THRESHOLD = 1

export interface UseSubFlowsOptions {
  nodes: Node[]
  edges: Edge[]
  setNodes: (nodes: Node[] | ((nodes: Node[]) => Node[])) => void
  setEdges: (edges: Edge[] | ((edges: Edge[]) => Edge[])) => void
  /** Enable auto-resize watching for groups */
  autoResizeOnChange?: boolean
  /** Configuration for subflow behavior */
  config?: Partial<SubflowConfig>
}

/** Auto-layout modes for arranging children */
export type AutoLayoutMode = 'none' | 'horizontal' | 'vertical' | 'grid'

export interface UseSubFlowsReturn {
  createGroup: (selectedNodeIds: string[], label?: string) => void
  extractChildren: (groupId: string) => void
  deleteGroupWithChildren: (groupId: string) => void
  toggleCollapse: (groupId: string) => void
  moveNodeToGroup: (nodeId: string, targetGroupId: string | null) => void
  autoResizeGroup: (groupId: string, animate?: boolean) => void
  autoResizeAllGroups: () => void
  ungroupNode: (nodeId: string) => void
  /** Find the best drop target for a dragged node */
  findDropTarget: (draggedNode: Node) => Node | null
  /** Check if a move would be valid (no circular reference) */
  isValidMove: (nodeId: string, targetGroupId: string) => boolean
  /** Mark a group as being resized (for animation class) */
  markResizing: (groupId: string, isResizing: boolean) => void
  /** Feature 7: Duplicate a group with all children */
  duplicateGroup: (groupId: string) => void
  /** Feature 8: Get all child node IDs for selection */
  getChildNodeIds: (groupId: string) => string[]
  /** Feature 6: Apply auto-layout to children */
  applyAutoLayout: (groupId: string, mode: AutoLayoutMode) => void
  /** Update group data properties */
  updateGroupData: (groupId: string, updates: Record<string, unknown>) => void
  /** Feature 9: Export group as PNG image */
  exportGroupAsImage: (groupId: string, format?: 'png' | 'svg') => Promise<void>
}

/**
 * Hook personalizado para gestionar sub-flows (grupos de nodos)
 *
 * Proporciona funciones para:
 * - Crear grupos desde nodos seleccionados
 * - Eliminar grupos (desagrupar)
 * - Colapsar/expandir grupos
 * - Mover nodos entre grupos
 * - Auto-redimensionar grupos (with smooth transitions)
 * - Desagrupar nodos individuales
 * - Find drop targets during drag operations
 *
 * Enhanced with overflow.dev-style grouping patterns:
 * - AABB bounding box calculation
 * - Smooth CSS transitions during resize
 * - Real-time drop target detection
 * - Circular reference prevention
 */
export function useSubFlows({
  nodes,
  edges,
  setNodes,
  setEdges,
  autoResizeOnChange = false,
  config: userConfig,
}: UseSubFlowsOptions): UseSubFlowsReturn {
  const { getNodesBounds } = useReactFlow()
  const config = { ...DEFAULT_SUBFLOW_CONFIG, ...userConfig }
  const resizingGroupsRef = useRef<Set<string>>(new Set())

  // Flag to prevent recursive resize calls
  const isResizingRef = useRef(false)

  // Debounce timers for each group
  const resizeTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  /**
   * Crea un nuevo grupo desde los nodos seleccionados
   * REGLA: Los StickyNote no pueden ser parte de un grupo
   */
  const createGroup = useCallback(
    (selectedNodeIds: string[], label?: string) => {
      if (selectedNodeIds.length === 0) return

      // Filtrar sticky notes de los nodos seleccionados
      const validNodeIds = selectedNodeIds.filter(id => {
        const node = nodes.find(n => n.id === id)
        return node && node.type !== 'sticky'
      })

      if (validNodeIds.length === 0) {
        console.warn('No se pueden crear grupos con solo StickyNotes')
        return
      }

      const groupId = `group-${Date.now()}`
      const groupLabel = label || `Grupo ${nodes.filter(n => n.type === 'subflow').length + 1}`

      setNodes((nds) => {
        return groupSelectedNodes(nds, validNodeIds, groupId, groupLabel)
      })
    },
    [nodes, setNodes]
  )

  /**
   * Extrae los nodos hijos de un grupo, convirtiéndolos en nodos raíz
   * IMPORTANTE: El grupo padre permanece vacío (no se elimina automáticamente)
   * Esto sigue el patrón preferido por el usuario, no el estándar de Figma
   */
  const extractChildren = useCallback(
    (groupId: string) => {
      setNodes((nds) => {
        const groupNode = nds.find((n) => n.id === groupId)
        if (!groupNode) return nds

        const childNodes = getChildNodes(nds, groupId)

        // Convertir posiciones relativas a absolutas y remover parentId
        const updatedNodes = nds.map((node) => {
          if (childNodes.some((child) => child.id === node.id)) {
            return {
              ...node,
              parentId: undefined,
              position: relativeToAbsolutePositionDeep(node.position, groupNode.id, nds),
              extent: undefined,
            }
          }
          return node
        })

        return sortNodesByHierarchy(updatedNodes)
      })
    },
    [setNodes]
  )

  /**
   * Elimina completamente un grupo junto con TODOS sus nodos hijos
   * ACCIÓN DESTRUCTIVA - Se debe confirmar con el usuario
   */
  const deleteGroupWithChildren = useCallback(
    (groupId: string) => {
      setNodes((nds) => {
        const childNodes = getChildNodes(nds, groupId)
        const childIds = new Set(childNodes.map((n) => n.id))

        // Remover el grupo Y todos sus hijos
        return nds.filter((n) => n.id !== groupId && !childIds.has(n.id))
      })
    },
    [setNodes]
  )

  /**
   * Colapsa o expande un grupo y sus descendientes
   */
  const toggleCollapse = useCallback(
    (groupId: string) => {
      let updatedForEdges: Node[] = nodes
      setNodes((nds) => {
        const groupNode = nds.find((n) => n.id === groupId)
        if (!groupNode) return nds

        const isCurrentlyCollapsed = groupNode.data?.collapsed || false
        const newCollapsedState = !isCurrentlyCollapsed

        // Actualizar el estado collapsed del grupo
        const updatedNodes = nds.map((n) =>
          n.id === groupId
            ? { ...n, data: { ...n.data, collapsed: newCollapsedState } }
            : n
        )

        // Ocultar/mostrar los nodos hijos
        const toggled = toggleGroupVisibility(updatedNodes, groupId, newCollapsedState)
        updatedForEdges = toggled
        return toggled
      })

      // Ocultar/mostrar edges conectadas a nodos ocultos
      setEdges((eds) => {
        return hideEdgesForHiddenNodes(eds, updatedForEdges)
      })
    },
    [nodes, setNodes, setEdges]
  )

  /**
   * Mueve un nodo a un grupo diferente (o lo libera si targetGroupId es null)
   * REGLA: Los StickyNote no pueden entrar en grupos (pero sí salir)
   *
   * FIXED: Properly calculates relative position to prevent "magnetic repulsion" effect
   */
  const moveNodeToGroup = useCallback(
    (nodeId: string, targetGroupId: string | null) => {
      setNodes((nds) => {
        const node = nds.find((n) => n.id === nodeId)
        const targetGroup = targetGroupId ? nds.find((n) => n.id === targetGroupId) : null

        if (!node) return nds

        // REGLA: Los StickyNote no pueden entrar en grupos
        // Solo permitir si está saliendo de un grupo (targetGroupId === null)
        if (node.type === 'sticky' && targetGroupId !== null) {
          console.warn(`StickyNote ${nodeId} no puede entrar en grupos`)
          return nds
        }

        // Si el nodo ya está en el grupo target, no hacer nada
        if (node.parentId === targetGroupId) return nds

        let updatedNodes = [...nds]

        // Get the absolute position of the node BEFORE any changes
        let absolutePos = node.position
        if (node.parentId) {
          absolutePos = relativeToAbsolutePositionDeep(node.position, node.parentId, nds)
        }

        // Remove from current parent first
        if (node.parentId) {
          updatedNodes = updatedNodes.map((n) =>
            n.id === nodeId
              ? { ...n, position: absolutePos, parentId: undefined, extent: undefined }
              : n
          )
        }

        // Si hay un nuevo grupo target, convertir a posición relativa
        if (targetGroup) {
          // Get the absolute position of the target group
          let groupAbsolutePos = targetGroup.position
          if (targetGroup.parentId) {
            groupAbsolutePos = relativeToAbsolutePositionDeep(
              targetGroup.position,
              targetGroup.parentId,
              updatedNodes
            )
          }

          // Calculate relative position: node position minus group position
          // This is the simple, correct calculation
          const relativePos = {
            x: absolutePos.x - groupAbsolutePos.x,
            y: absolutePos.y - groupAbsolutePos.y,
          }

          // Ensure the node is within the group bounds (with padding)
          const groupWidth = (targetGroup.style?.width as number) || config.minWidth
          const groupHeight = (targetGroup.style?.height as number) || config.minHeight
          const padding = config.padding

          // Clamp position to be within group bounds
          const clampedPos = {
            x: Math.max(padding, Math.min(relativePos.x, groupWidth - padding - 150)), // 150 = estimated node width
            y: Math.max(padding, Math.min(relativePos.y, groupHeight - padding - 40)),  // 40 = estimated node height
          }

          updatedNodes = updatedNodes.map((n) =>
            n.id === nodeId
              ? {
                  ...n,
                  parentId: targetGroupId ?? undefined,
                  position: clampedPos,
                  extent: 'parent' as const,
                }
              : n
          )
        }

        return sortNodesByHierarchy(updatedNodes)
      })
    },
    [setNodes, config]
  )

  /**
   * Auto-redimensiona un grupo basándose en sus nodos hijos
   * Calcula el bounding box de los hijos y ajusta tamaño y posición del parent
   * Enhanced with overflow.dev-style smooth transitions
   *
   * FIXED: Prevents recursive resize loop by:
   * 1. Using isResizingRef to prevent concurrent calls
   * 2. Only adjusting child positions when delta exceeds threshold
   * 3. Not moving the group if children are already inside bounds
   *
   * @param groupId - The ID of the group to resize
   * @param animate - Whether to animate the resize (default: true)
   */
  const autoResizeGroup = useCallback(
    (groupId: string, animate: boolean = true) => {
      // Prevent recursive calls
      if (isResizingRef.current) {
        return
      }

      // Cancel any pending resize for this group
      const existingTimer = resizeTimersRef.current.get(groupId)
      if (existingTimer) {
        clearTimeout(existingTimer)
      }

      // Debounce the resize operation
      const timer = setTimeout(() => {
        isResizingRef.current = true

        try {
          setNodes((nds) => {
            const groupNode = nds.find((n) => n.id === groupId)
            if (!groupNode || groupNode.type !== 'subflow') return nds

            // Check if group is collapsed - don't resize collapsed groups
            if (groupNode.data?.collapsed) return nds

            const childNodes = getChildNodes(nds, groupId)

            // Si no hay hijos, aplicar tamaño mínimo sin mover el grupo
            if (childNodes.length === 0) {
              const currentWidth = (groupNode.style?.width as number) || config.minWidth
              const currentHeight = (groupNode.style?.height as number) || config.minHeight

              // Only update if size actually needs to change
              if (currentWidth >= config.minWidth && currentHeight >= config.minHeight) {
                return nds
              }

              return nds.map((n) =>
                n.id === groupId
                  ? {
                      ...n,
                      style: {
                        ...n.style,
                        width: config.minWidth,
                        height: config.minHeight,
                      },
                    }
                  : n
              )
            }

            // Calculate bounds based on children positions (relative to group)
            const bounds = calculateChildrenBounds(childNodes)
            const padding = config.padding

            // Calculate required size to contain all children
            const requiredWidth = Math.max(bounds.maxX + padding, config.minWidth)
            const requiredHeight = Math.max(bounds.maxY + padding, config.minHeight)

            // Get current group size
            const currentWidth = (groupNode.style?.width as number) || config.minWidth
            const currentHeight = (groupNode.style?.height as number) || config.minHeight

            // Check if any child is outside the current bounds (negative position)
            const hasChildOutsideBounds = bounds.minX < padding || bounds.minY < padding

            // If children are all inside bounds and size is adequate, do nothing
            if (!hasChildOutsideBounds &&
                currentWidth >= requiredWidth &&
                currentHeight >= requiredHeight) {
              return nds
            }

            // Calculate how much we need to adjust
            let deltaX = 0
            let deltaY = 0
            let newWidth = currentWidth
            let newHeight = currentHeight

            // If a child is at negative position, we need to move the group AND adjust children
            if (bounds.minX < padding) {
              deltaX = bounds.minX - padding
            }
            if (bounds.minY < padding) {
              deltaY = bounds.minY - padding
            }

            // Calculate new size (only grow, never shrink below current)
            newWidth = Math.max(currentWidth, requiredWidth - deltaX)
            newHeight = Math.max(currentHeight, requiredHeight - deltaY)

            // If delta is too small, ignore it (prevents micro-adjustments)
            if (Math.abs(deltaX) < POSITION_THRESHOLD && Math.abs(deltaY) < POSITION_THRESHOLD) {
              // Only update size if needed
              if (newWidth === currentWidth && newHeight === currentHeight) {
                return nds
              }

              // Just resize without moving
              return nds.map((n) =>
                n.id === groupId
                  ? {
                      ...n,
                      style: {
                        ...n.style,
                        width: newWidth,
                        height: newHeight,
                      },
                    }
                  : n
              )
            }

            // Move group and adjust children positions
            return nds.map((n) => {
              if (n.id === groupId) {
                return {
                  ...n,
                  position: {
                    x: groupNode.position.x + deltaX,
                    y: groupNode.position.y + deltaY,
                  },
                  style: {
                    ...n.style,
                    width: newWidth,
                    height: newHeight,
                  },
                }
              }

              // Adjust child positions to compensate for group movement
              if (n.parentId === groupId) {
                return {
                  ...n,
                  position: {
                    x: n.position.x - deltaX,
                    y: n.position.y - deltaY,
                  },
                }
              }

              return n
            })
          })
        } finally {
          // Reset flag after a short delay to allow React to process
          setTimeout(() => {
            isResizingRef.current = false
          }, 50)
        }

        resizeTimersRef.current.delete(groupId)
      }, 16) // ~1 frame debounce

      resizeTimersRef.current.set(groupId, timer)
    },
    [setNodes, config]
  )

  /**
   * Auto-resize all groups that contain the specified nodes
   * Resizes from deepest to shallowest to ensure proper nesting
   */
  const autoResizeAllGroups = useCallback(() => {
    setNodes((nds) => {
      const allGroupIds = nds.filter(n => n.type === 'subflow').map(n => n.id)
      const sortedGroupIds = getGroupsToResize(allGroupIds, nds)

      let updatedNodes = [...nds]
      sortedGroupIds.forEach(groupId => {
        updatedNodes = recalculateGroupWithChildren(updatedNodes, groupId, config)
      })

      return updatedNodes
    })
  }, [setNodes, config])

  /**
   * Desagrupa un nodo individual, convirtiéndolo de hijo a nodo raíz
   */
  const ungroupNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => {
        const node = nds.find((n) => n.id === nodeId)
        if (!node || !node.parentId) return nds

        const parentId = node.parentId

        // Convertir posición relativa a absoluta considerando ancestros
        const absolutePos = relativeToAbsolutePositionDeep(node.position, node.parentId, nds)

        const updatedNodes = sortNodesByHierarchy(
          nds.map((n) =>
            n.id === nodeId
              ? {
                  ...n,
                  parentId: undefined,
                  position: absolutePos,
                  extent: undefined,
                }
              : n
          )
        )

        return updatedNodes
      })
    },
    [setNodes]
  )

  /**
   * Find the best drop target for a dragged node
   * Returns the deepest valid group that contains the node's center
   */
  const findDropTarget = useCallback(
    (draggedNode: Node): Node | null => {
      return findBestDropTarget(draggedNode, nodes, {
        maxNestingLevel: 5,
      })
    },
    [nodes]
  )

  /**
   * Check if moving a node to a target group would be valid
   * (no circular references, proper nesting level, etc.)
   */
  const isValidMove = useCallback(
    (nodeId: string, targetGroupId: string): boolean => {
      return !wouldCreateCircularReference(nodeId, targetGroupId, nodes)
    },
    [nodes]
  )

  /**
   * Mark a group as being resized (for animation CSS class)
   */
  const markResizing = useCallback(
    (groupId: string, isResizing: boolean) => {
      if (isResizing) {
        resizingGroupsRef.current.add(groupId)
      } else {
        resizingGroupsRef.current.delete(groupId)
      }

      setNodes((nds) =>
        nds.map((n) =>
          n.id === groupId
            ? { ...n, data: { ...n.data, isResizing } }
            : n
        )
      )
    },
    [setNodes]
  )

  /**
   * Feature 7: Duplicate a group with all its children
   * Creates a copy of the group and all its child nodes, offset by 50px
   */
  const duplicateGroup = useCallback(
    (groupId: string) => {
      setNodes((nds) => {
        const groupNode = nds.find((n) => n.id === groupId)
        if (!groupNode || groupNode.type !== 'subflow') return nds

        const childNodes = getChildNodes(nds, groupId)
        const timestamp = Date.now()

        // Create ID mapping for children
        const idMapping = new Map<string, string>()
        idMapping.set(groupId, `group-${timestamp}`)
        childNodes.forEach((child, index) => {
          idMapping.set(child.id, `${child.type || 'node'}-${timestamp}-${index}`)
        })

        // Clone the group node with offset
        const clonedGroup = {
          ...groupNode,
          id: idMapping.get(groupId)!,
          position: {
            x: groupNode.position.x + 50,
            y: groupNode.position.y + 50,
          },
          data: {
            ...groupNode.data,
            label: `${groupNode.data?.label || 'Grupo'} (copia)`,
          },
          selected: false,
        }

        // Clone all child nodes
        const clonedChildren = childNodes.map((child) => ({
          ...child,
          id: idMapping.get(child.id)!,
          parentId: idMapping.get(groupId)!,
          selected: false,
        }))

        return sortNodesByHierarchy([...nds, clonedGroup, ...clonedChildren])
      })

      // Also duplicate edges between children
      setEdges((eds) => {
        const childIds = new Set(
          nodes
            .filter((n) => n.parentId === groupId)
            .map((n) => n.id)
        )

        const edgesToDuplicate = eds.filter(
          (e) => childIds.has(e.source) && childIds.has(e.target)
        )

        if (edgesToDuplicate.length === 0) return eds

        const timestamp = Date.now()
        const clonedEdges = edgesToDuplicate.map((edge, index) => ({
          ...edge,
          id: `edge-${timestamp}-${index}`,
          // Note: source/target IDs would need to be mapped to new IDs
          // This is a simplified version - full implementation would need ID mapping
        }))

        return [...eds, ...clonedEdges]
      })
    },
    [nodes, setNodes, setEdges]
  )

  /**
   * Feature 8: Get all child node IDs for selection
   * Returns an array of child node IDs that can be used to set selection
   */
  const getChildNodeIds = useCallback(
    (groupId: string): string[] => {
      return nodes
        .filter((n) => n.parentId === groupId)
        .map((n) => n.id)
    },
    [nodes]
  )

  /**
   * Feature 6: Apply auto-layout to children of a group
   * Arranges children in horizontal, vertical, or grid layout
   */
  const applyAutoLayout = useCallback(
    (groupId: string, mode: 'none' | 'horizontal' | 'vertical' | 'grid') => {
      if (mode === 'none') return

      setNodes((nds) => {
        const groupNode = nds.find((n) => n.id === groupId)
        if (!groupNode || groupNode.type !== 'subflow') return nds

        const childNodes = getChildNodes(nds, groupId)
        if (childNodes.length === 0) return nds

        const padding = config.padding
        const gap = 20 // Gap between nodes
        const nodeWidth = 150 // Estimated node width
        const nodeHeight = 40 // Estimated node height

        // Sort children by their current position (left-to-right, top-to-bottom)
        const sortedChildren = [...childNodes].sort((a, b) => {
          if (mode === 'horizontal') return a.position.x - b.position.x
          if (mode === 'vertical') return a.position.y - b.position.y
          // Grid: sort by y first, then x
          const rowA = Math.floor(a.position.y / (nodeHeight + gap))
          const rowB = Math.floor(b.position.y / (nodeHeight + gap))
          if (rowA !== rowB) return rowA - rowB
          return a.position.x - b.position.x
        })

        // Calculate new positions based on layout mode
        const newPositions = new Map<string, { x: number; y: number }>()

        if (mode === 'horizontal') {
          let currentX = padding
          sortedChildren.forEach((child) => {
            newPositions.set(child.id, { x: currentX, y: padding })
            currentX += nodeWidth + gap
          })
        } else if (mode === 'vertical') {
          let currentY = padding
          sortedChildren.forEach((child) => {
            newPositions.set(child.id, { x: padding, y: currentY })
            currentY += nodeHeight + gap
          })
        } else if (mode === 'grid') {
          const columns = Math.ceil(Math.sqrt(sortedChildren.length))
          sortedChildren.forEach((child, index) => {
            const col = index % columns
            const row = Math.floor(index / columns)
            newPositions.set(child.id, {
              x: padding + col * (nodeWidth + gap),
              y: padding + row * (nodeHeight + gap),
            })
          })
        }

        // Update node positions
        const updatedNodes = nds.map((n) => {
          const newPos = newPositions.get(n.id)
          if (newPos) {
            return { ...n, position: newPos }
          }
          return n
        })

        return updatedNodes
      })

      // Auto-resize after layout
      setTimeout(() => {
        autoResizeGroup(groupId, true)
      }, 50)
    },
    [setNodes, config, autoResizeGroup]
  )

  /**
   * Update group data properties (color, description, locked, opacity, padding, etc.)
   */
  const updateGroupData = useCallback(
    (groupId: string, updates: Record<string, unknown>) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === groupId
            ? { ...n, data: { ...n.data, ...updates } }
            : n
        )
      )
    },
    [setNodes]
  )

  /**
   * Feature 9: Export group as PNG or SVG image
   * Finds the DOM element for the group node and exports it
   */
  const exportGroupAsImage = useCallback(
    async (groupId: string, format: 'png' | 'svg' = 'png'): Promise<void> => {
      const groupNode = nodes.find((n) => n.id === groupId)
      if (!groupNode) {
        console.error(`Group ${groupId} not found`)
        return
      }

      // Find the DOM element for the group node
      const element = document.querySelector(`[data-id="${groupId}"]`) as HTMLElement
      if (!element) {
        console.error(`DOM element for group ${groupId} not found`)
        return
      }

      try {
        // Generate the image
        const options = {
          backgroundColor: '#ffffff',
          pixelRatio: 2, // Higher quality
          filter: (node: HTMLElement) => {
            // Filter out resize handles and other UI elements
            const className = node.className || ''
            if (typeof className === 'string') {
              if (className.includes('react-flow__resize-control')) return false
              if (className.includes('nodrag')) return false
            }
            return true
          },
        }

        let dataUrl: string
        const htmlToImage = await import('html-to-image')
        if (format === 'svg') {
          dataUrl = await htmlToImage.toSvg(element, options)
        } else {
          dataUrl = await htmlToImage.toPng(element, options)
        }

        // Download the image
        const link = document.createElement('a')
        link.download = `${groupNode.data?.label || 'grupo'}-${Date.now()}.${format}`
        link.href = dataUrl
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('Error exporting group as image:', error)
      }
    },
    [nodes]
  )

  return {
    createGroup,
    extractChildren,
    deleteGroupWithChildren,
    toggleCollapse,
    moveNodeToGroup,
    autoResizeGroup,
    autoResizeAllGroups,
    ungroupNode,
    findDropTarget,
    isValidMove,
    markResizing,
    // New features
    duplicateGroup,
    getChildNodeIds,
    applyAutoLayout,
    updateGroupData,
    exportGroupAsImage,
  }
}
